import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPR } from '../context/APRContext';

const CostingOverview: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAPR();
  const { dashboardStats, currentRunRateData, loading } = state;

  // Debug logging removed

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Costing Overview</h1>
          <p>Loading costing data...</p>
        </div>
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <div>⏳ Loading financial data from the system...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Costing Overview</h1>
        <p>Comprehensive view of application portfolio costs, projects, migrations, and future projections</p>
      </div>

      {/* Debug Info Panel */}
      <div style={{
        margin: '15px',
        padding: '10px',
        backgroundColor: '#f0f8ff',
        border: '1px solid #b0d4f1',
        borderRadius: '8px',
        fontSize: '12px',
        fontFamily: 'monospace'
      }}>
        <strong>🔍 Debug Info:</strong><br/>
        Loading: {loading ? 'Yes' : 'No'} | 
        Current Run Rate Apps: {currentRunRateData?.length || 0} | 
        Total Annual Cost: {formatCurrency(dashboardStats.totalCosts.current_run_rate)}
        {currentRunRateData?.length > 0 && (
          <div>Sample App: {currentRunRateData[0]?.applicationName} - {formatCurrency(currentRunRateData[0]?.totalAnnualCost || 0)}</div>
        )}
      </div>

      <div className="dashboard-grid">
        {/* Cost Analysis - Same as Dashboard */}
        <div className="dashboard-section">
          <h2>Cost Analysis</h2>
          <div className="cost-grid">
            <div className="cost-card">
              <div className="cost-header">
                <h3>Current Run Rate</h3>
                <span className="cost-icon">💰</span>
              </div>
              <div className="cost-value">
                {formatCurrency(dashboardStats.totalCosts.current_run_rate)}
              </div>
              <div className="cost-subtitle">Annual operational costs</div>
              <div className="cost-actions">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate('/customers/costing/current-run-rate')}
                >
                  View Details →
                </button>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Planned Investment</h3>
                <span className="cost-icon">📊</span>
              </div>
              <div className="cost-value">
                {formatCurrency(dashboardStats.totalCosts.planned_cost)}
              </div>
              <div className="cost-subtitle">One-time improvement costs</div>
              <div className="cost-actions">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate('/customers/costing/planned-projects')}
                >
                  View Details →
                </button>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Migration Costs</h3>
                <span className="cost-icon">🔄</span>
              </div>
              <div className="cost-value">
                {formatCurrency(dashboardStats.totalCosts.migration_conversion)}
              </div>
              <div className="cost-subtitle">Rationalization implementation</div>
              <div className="cost-actions">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate('/customers/costing/migration-conversion')}
                >
                  View Details →
                </button>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Future Run Rate</h3>
                <span className="cost-icon">🎯</span>
              </div>
              <div className="cost-value">
                {formatCurrency(dashboardStats.totalCosts.future_run_rate)}
              </div>
              <div className="cost-subtitle">Post-rationalization annual costs</div>
              <div className="cost-actions">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate('/customers/costing/future-run-rates')}
                >
                  View Details →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Application Portfolio Summary */}
        <div className="dashboard-section">
          <h2>Portfolio Summary</h2>
          <div className="card-grid">
            <div className="overview-card">
              <div className="card-header">
                <h3>Total Applications</h3>
                <span className="card-icon">📱</span>
              </div>
              <div className="card-value">{dashboardStats.totalApplications}</div>
              <div className="card-subtitle">Applications in portfolio</div>
            </div>
            <div className="overview-card">
              <div className="card-header">
                <h3>Current Run Rate Applications</h3>
                <span className="card-icon">💰</span>
              </div>
              <div className="card-value">{state.currentRunRateData.length}</div>
              <div className="card-subtitle">With cost data available</div>
            </div>
          </div>
        </div>

        {/* Quick Actions for Costing */}
        <div className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing/current-run-rate')}
            >
              <span className="action-icon">📊</span>
              <span>Update Run Rates</span>
            </button>
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing/planned-projects')}
            >
              <span className="action-icon">📋</span>
              <span>Manage Projects</span>
            </button>
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing/migration-conversion')}
            >
              <span className="action-icon">🔄</span>
              <span>Plan Migrations</span>
            </button>
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing/future-run-rates')}
            >
              <span className="action-icon">📈</span>
              <span>Project Future</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostingOverview;